import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import PhoneOtpLogin from "@/components/PhoneOtpLogin";

const AdminLogin = () => {
  const [email, setEmail] = useState("careanywhereagency@gmail.com");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Signed in.");
      nav("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  const onOtpSuccess = ({ access_token }) => {
    localStorage.setItem("ca_token", access_token);
    toast.success("Signed in.");
    // Force AuthContext to refresh
    window.location.href = "/admin";
  };

  return (
    <main data-testid="admin-login-page" className="min-h-screen flex flex-col bg-background">
      <div className="p-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
        >
          <ArrowLeft size={16} /> Back to site
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-md rounded-2xl border border-border/70 bg-card p-8 md:p-10 shadow-[0_20px_60px_-30px_rgba(63,109,138,0.28)]">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
              <ShieldCheck size={18} />
            </div>
            <div>
              <div className="text-xs tracking-[0.25em] uppercase text-primary/80">Care Anywhere</div>
              <div className="font-serif-display text-2xl">Admin sign-in</div>
            </div>
          </div>

          <Tabs defaultValue="password" className="w-full">
            <TabsList className="grid grid-cols-2 mb-6 rounded-full bg-muted p-1 h-11">
              <TabsTrigger value="password" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="admin-tab-password">Email &amp; password</TabsTrigger>
              <TabsTrigger value="otp" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="admin-tab-otp">Text me a code</TabsTrigger>
            </TabsList>

            <TabsContent value="password">
              <form onSubmit={onSubmit} data-testid="admin-login-form" className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
                  <Input
                    id="email"
                    data-testid="input-admin-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 h-11 rounded-lg"
                    autoComplete="username"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-xs uppercase tracking-widest text-muted-foreground">Password</Label>
                  <Input
                    id="password"
                    data-testid="input-admin-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-2 h-11 rounded-lg"
                    autoComplete="current-password"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="admin-login-submit"
                  disabled={busy}
                  className="mt-4 w-full rounded-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  {busy ? "Signing in…" : "Sign in"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="otp">
              <PhoneOtpLogin purpose="admin" onSuccess={onOtpSuccess} allowSignup={false} />
            </TabsContent>
          </Tabs>

          <p className="mt-6 text-xs text-center text-muted-foreground">
            Restricted to Care Anywhere staff.
          </p>
        </div>
      </div>
    </main>
  );
};

export default AdminLogin;
